import { defineStore } from 'pinia'
import { cmsContent } from './cmsDef'
/**
 * Pinia store for CMS-managed UI content.
 *
 * This store provides access to singleton and collection CMS items, as defined in `cmsContent`.
 * It supports type-safe retrieval of data used for building dynamic UI components like menus, footers, infoboxes, etc.
 *
 * ---
 * * State:
 * - `cms`: Default CMS structure including both `singleton` and `collection` groups, as defined in `cmsContent`.
 *
 * ---
 * * Getters:
 * - `getCollection(key: string): any[]`
 *   Retrieves a CMS collection payload by key. Returns an empty array if the key is missing or not yet loaded.
 *
 * - `getSingleton(key: string): Record<string, any>`
 *   Retrieves a CMS singleton payload by key. Returns an empty object if the key is missing or not yet loaded.
 * ---
 * * Actions:
 * - `fetchCMSItem(group: 'singleton' | 'collection', key: string): Promise<void>`
 * - `fetchAllCMSContent(): Promise<void>`
 * - `fetchSingletonEndpoint(key: string): Promise<void>`
 * - `fetchCollectionEndpoint(key: string): Promise<void>`
 * @module useCMSStore
 */
export const useCMSStore = defineStore('CMSStore', {
  state: () => ({
    /**
   * CMS content organized by type.
   * @type {{ singleton: Record<string, CmsItem>, collection: Record<string, CmsItem> }}
   */
    cms: cmsContent
  }),
  getters: {
    /**
     * Get a collection-type CMS content value by key.
     *
     * @param {Object} state - The current state of the store.
     * @returns {Function} Getter function that accepts a `key` and returns the associated collection value.
     */
    getCollection: (state) => (key) => {
      return state.cms.collection[key]?.value ?? []
    },
    /**
     * Get a singleton-type CMS content value by key.
     *
     * @param {Object} state - The current state of the store.
     * @returns {Function} Getter function that accepts a `key` and returns the associated singleton value.
     */
    getSingleton: (state) => (key) => {
      return state.cms.singleton[key]?.value ?? {}
    }
  },
  actions: {
    /**
     * Fetches a specific CMS item (singleton or collection) by key.
     * Automatically updates `isLoading`, `value`, and `error` fields.
     *
     * @param {'singleton' | 'collection'} group - The group to fetch from.
     * @param {string} key - The item key to fetch.
     */
    async fetchCMSItem(group, key) {
      const item = this.cms[group]?.[key]
      const path = item.endpoint
      if(!item){
        console.warn(`CMS item not found: group="${group}", key="${key}"`)
        return
      }
      try {
        item.isLoading = true
        const url = `${import.meta.env.VITE_COCKPIT_URL}${path}?token=${import.meta.env.VITE_COCKPIT_TOKEN}`
        
        const cmsResponse = await fetch(url)

        if (!cmsResponse.ok) {
          throw new Error(`Error fetching ${key}: ${cmsResponse.status}`)
        }

        item.value = await cmsResponse.json()
      } catch (error) {
        console.error(`Error fetching ${key}:`, error)
        item.error = error
      } finally {
        item.isLoading = false
      }
    },
    /**
     * Fetches all CMS content in both `singleton` and `collection` groups.
     * Executes requests in parallel within each group.
     */
    async fetchAllCMSContent() {
      const groups = ['singleton', 'collection']

      await Promise.all(
        groups.map(async (group) => {
          const keys = Object.keys(this.cms[group])
          await Promise.all(
            keys.map((key) => this.fetchCMSItem(group, key))
          )
        })
      )
    },
    /**
     * Fetches a singleton CMS item by key.
     * @param {string} key - The singleton item key to fetch.
     */
    async fetchSingletonEndpoint(key) {
      await this.fetchCMSItem('singleton', key)
    },
    /**
     * Fetches a collection CMS item by key.
     * @param {string} key - The collection item key to fetch.
     */
    async fetchCollectionEndpoint(key) {
      await this.fetchCMSItem('collection', key)
    }
  }
})
